import {
  createFileRoute,
  Outlet,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { Registry } from '@/pages/registry';
import { commonSearchSchema } from '@/routes/-shared/commonSearchSchema';
import { RoleGuard } from '@/components/molecules/role-guard';
import { usePermissions } from '@/hooks/use-permissions';
import z from '@/lib/zod';
import {
  optionalSearchBoolean,
  optionalSearchString,
} from '@/lib/zod-search-params';
import { useEffect } from 'react';
import { PROTECTED, REGISTRY } from '@/lib/constants/routes';
import { resolveAuthorizedTab } from '@/lib/permissions/resolve-authorized-tab';

export const registrySearchSchema = commonSearchSchema.extend({
  tab: z.string().nullish(),
  /**
   * Scheda aperta nel pannello della controparte (Anagrafica, Riepilogo,
   * Storico, Articoli, Incassi): `?tab=` è già della pagina.
   */
  section: optionalSearchString(),
  /**
   * Filtri «Tipo», «Ruolo» e «Paese» della lista Controparti. Vanno dichiarati
   * qui: zod strippa dall'URL ogni parametro fuori schema, e al refresh il
   * filtro sparirebbe senza che la pill se ne accorga.
   *
   * Il ruolo si chiama `companyRole` e non `role`: vedi `COUNTERPART_ROLE_PARAM`
   * in `pages/registry/shared/table/registry-api-filters.ts`.
   */
  companyType: optionalSearchString(),
  companyRole: optionalSearchString(),
  country: optionalSearchString(),
  /**
   * Pannello Filtri della lista Prodotti (tavola 8a), con i nomi dell'API:
   * vedi `pages/products/table/product-filters.ts`.
   */
  categoryId: optionalSearchString(),
  productType: optionalSearchString(),
  includeObsolete: optionalSearchBoolean(),
  includeHidden: optionalSearchBoolean(),
  activeOnly: optionalSearchBoolean(),
});

export type RegistrySearchSchema = z.infer<typeof registrySearchSchema>;

export const Route = createFileRoute('/protected/_layout/registry/_layout')({
  validateSearch: registrySearchSchema,
  component: RegistryLayout,
});

function RegistryLayout() {
  const { canAccessRegistry, canAccessProducts, isLoading } = usePermissions();
  const navigate = useNavigate();
  const search = useSearch({
    from: '/protected/_layout/registry/_layout',
  });
  const requestedTab = search.tab === 'products' ? 'products' : 'counterparts';
  const accessState = resolveAuthorizedTab(requestedTab, [
    { value: 'counterparts', hasAccess: canAccessRegistry },
    { value: 'products', hasAccess: canAccessProducts },
  ]);

  useEffect(() => {
    if (
      isLoading ||
      accessState.hasRequestedAccess ||
      !accessState.fallbackTab
    ) {
      return;
    }
    navigate({
      to: `${PROTECTED}${REGISTRY}`,
      // `companyType`, `companyRole` e `country` filtrano le sole Controparti:
      // portarseli sul tab di ripiego restringerebbe una lista che non ha quei
      // filtri, senza che nulla lo dica (stessa convenzione del cambio tab a
      // mano).
      search: (previous) => ({
        ...previous,
        tab: accessState.fallbackTab,
        companyType: undefined,
        companyRole: undefined,
        country: undefined,
      }),
      replace: true,
    });
  }, [
    accessState.fallbackTab,
    accessState.hasRequestedAccess,
    isLoading,
    navigate,
  ]);

  if (
    !isLoading &&
    accessState.hasAnyAccess &&
    !accessState.hasRequestedAccess
  ) {
    return null;
  }

  return (
    <RoleGuard hasAccess={accessState.hasRequestedAccess} isLoading={isLoading}>
      <Registry />
      <Outlet />
    </RoleGuard>
  );
}
